import { SCIENCE_DEFINITIONS, EXCLUSIVE_SCIENCE_IDS, NON_RUSHABLE_SCIENCE_IDS, SURPRISE_TACTICS_CRIT_CHANCE_PER_LEVEL, } from "@kaeliux/shared";
import { calculateBuildingCostReduction, meetsRequirements } from "./buildings.js";
import { calculatePhysicsMultiplier, calculateOzoneRecovery } from "./resourceEngine.js";
import { calculateMathematicsMultiplier } from "./resources.js";
// ---------------------------------------------------------------------------
// Cost
// ---------------------------------------------------------------------------
const COST_KEYS = ["metal", "minerals", "gas", "energy"];
/**
 * The closest step at or before `level` — steps are ordered ascending by
 * `atLevel`, and every science has one at `atLevel: 1`, so this always
 * resolves.
 */
function resolveStep(researchCost, level) {
    const first = researchCost[0];
    if (!first)
        throw new Error("Science definition has no researchCost steps");
    let chosen = first;
    for (const step of researchCost) {
        if (step.atLevel > level)
            break;
        chosen = step;
    }
    return chosen;
}
/**
 * Cost to research `scienceId` at `level` (level >= 1, i.e. the cost to go
 * from `level - 1` to `level`).
 *
 *   cost(level) = step.cost × 2^(level - step.atLevel), rounded per resource key
 *
 * where `step` is the closest {@link ScienceCostStep} at or before `level` —
 * see `researchCost` on `ScienceDefinition`.
 */
export function calculateScienceCost(scienceId, level) {
    const def = SCIENCE_DEFINITIONS[scienceId];
    if (!def)
        throw new Error(`Unknown science: ${scienceId}`);
    if (level < 1)
        throw new Error(`Science level must be >= 1, got ${level}`);
    const step = resolveStep(def.researchCost, level);
    const exponent = level - step.atLevel;
    const cost = {};
    for (const key of COST_KEYS) {
        const base = step.cost[key];
        if (base === undefined)
            continue;
        cost[key] = Math.round(base * Math.pow(2, exponent));
    }
    return cost;
}
/**
 * Free Energy the planet must have to START researching `scienceId` — see
 * `requiredFreeEnergy` on `ScienceDefinition`. 0 when the science has no gate.
 */
export function getScienceRequiredFreeEnergy(scienceId) {
    return SCIENCE_DEFINITIONS[scienceId]?.requiredFreeEnergy ?? 0;
}
export function hasRequiredFreeEnergy(scienceId, freeEnergy) {
    const required = getScienceRequiredFreeEnergy(scienceId);
    return required <= 0 || freeEnergy >= required;
}
// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------
const SCIENCE_TIME_LEVEL_MULTIPLIER = 1.5;
const LAB_REDUCTION_PER_LEVEL_EARLY = 0.03; // levels 1-10
const LAB_REDUCTION_PER_LEVEL_LATE = 0.04; // levels 11-20
const LAB_REDUCTION_LATE_BASE = 0.30;
const LAB_REDUCTION_CAP = 0.70;
/**
 * Fraction the Research Center building reduces research time by, per
 * `docs/game-details/sciences.md` "Research Building Reduction Formula".
 */
export function calculateLaboratoryReduction(laboratoryLevel) {
    const level = Math.max(0, laboratoryLevel);
    const reduction = level <= 10
        ? level * LAB_REDUCTION_PER_LEVEL_EARLY
        : LAB_REDUCTION_LATE_BASE + (level - 10) * LAB_REDUCTION_PER_LEVEL_LATE;
    return Math.min(LAB_REDUCTION_CAP, reduction);
}
/**
 * Research duration for `scienceId` at `level` (level >= 1), in seconds.
 *
 *   time(level) = step.timeSeconds × 1.5^(level - step.atLevel)
 *   result      = floor( time(level) × (1 − labReduction) / speedMultiplier )
 *
 * where `step` is the same closest-at-or-before-`level` {@link ScienceCostStep}
 * `calculateScienceCost` resolves — a step exists to record a time anomaly
 * as well as (or instead of) a cost one, though none of today's sciences
 * have one; every non-Level-1 step so far continues the plain formula.
 *
 * `speedMultiplier` is the universe's x-speed (defaults to 1x) — the doc's
 * formula folds in `server_speed^-1` the same way building/ship durations
 * already scale by `UniverseContext.speedMultiplier` elsewhere.
 */
export function calculateScienceTime(scienceId, level, laboratoryLevel, speedMultiplier = 1) {
    const def = SCIENCE_DEFINITIONS[scienceId];
    if (!def)
        throw new Error(`Unknown science: ${scienceId}`);
    if (level < 1)
        throw new Error(`Science level must be >= 1, got ${level}`);
    const step = resolveStep(def.researchCost, level);
    const rawTime = step.timeSeconds * Math.pow(SCIENCE_TIME_LEVEL_MULTIPLIER, level - step.atLevel);
    const reduction = calculateLaboratoryReduction(laboratoryLevel);
    const speed = speedMultiplier > 0 ? speedMultiplier : 1;
    return Math.max(1, Math.floor((rawTime * (1 - reduction)) / speed));
}
// ---------------------------------------------------------------------------
// Requirements / upgrade validity
// ---------------------------------------------------------------------------
export function getMaxScienceLevel(scienceId) {
    return SCIENCE_DEFINITIONS[scienceId]?.maxLevel ?? 0;
}
/**
 * Check if a science's prerequisites (Research Center level and/or other science
 * levels) are met. Same tagged-union shape as building requirements.
 */
export function checkScienceRequirements(scienceId, buildingLevels, scienceLevels = {}) {
    const def = SCIENCE_DEFINITIONS[scienceId];
    if (!def || def.requirements.length === 0)
        return true;
    return meetsRequirements(def.requirements, buildingLevels, scienceLevels);
}
/**
 * Only one of the mutually-exclusive tactics sciences (Offensive Tactics /
 * Defensive Tactics / Surprise Tactics — see `EXCLUSIVE_SCIENCE_IDS`) may
 * ever be researched per player. Continuing an already-started one is
 * always allowed; starting a *different* one is blocked once any of them
 * has level > 0.
 */
/** `gravitationalManipulators` can never be rushed — see `NON_RUSHABLE_SCIENCE_IDS`'s doc comment. */
export function isScienceRushable(scienceId) {
    return !NON_RUSHABLE_SCIENCE_IDS.includes(scienceId);
}
export function canResearchAdditionalScience(scienceId, scienceLevels) {
    if (!EXCLUSIVE_SCIENCE_IDS.includes(scienceId))
        return true;
    if ((scienceLevels[scienceId] ?? 0) > 0)
        return true;
    return !EXCLUSIVE_SCIENCE_IDS
        .filter((id) => id !== scienceId)
        .some((id) => (scienceLevels[id] ?? 0) > 0);
}
/**
 * Check if a science can be upgraded: max level, prerequisites, and (for
 * the mutually-exclusive tactics sciences) the single-per-player rule.
 */
export function canUpgradeScience(scienceId, currentLevel, buildingLevels, scienceLevels = {}) {
    const maxLevel = getMaxScienceLevel(scienceId);
    if (maxLevel > 0 && currentLevel >= maxLevel)
        return false;
    if (!checkScienceRequirements(scienceId, buildingLevels, scienceLevels))
        return false;
    if (!canResearchAdditionalScience(scienceId, scienceLevels))
        return false;
    return true;
}
/** `min(level, maxLevel) × perLevel` — additive (non-multiplier-base-1) bonuses, e.g. chances or flat reductions. */
function cappedAdditive(level, perLevel, maxLevel) {
    return Math.min(Math.max(level, 0), maxLevel) * perLevel;
}
/** `1 + cappedAdditive(...)` — the shape most "+X% per level, capped" multiplier bonuses use. */
function linearBonus(level, perLevel, maxLevel) {
    return 1 + cappedAdditive(level, perLevel, maxLevel);
}
/**
 * Compute every science's current bonus value from a player's researched
 * levels. Pure data — callers decide where/whether to apply each value
 * (e.g. Physics already feeds `calculatePlanetTick` via `TickContext`;
 * Mathematics feeds `calculateTotalProduction`; Ultimate Building feeds
 * `calculateBuildingUpgradeCost`; combat/fleet/UI systems consume the rest
 * as they land).
 *
 * Note: the docs list Ion's cap as "255%"; every other 15%-per-level,
 * 15-level-max science (Laser/Plasma/Rocket Fuel/Aerodynamics) caps at 225%
 * (15 × 15%), so 255% is treated as a documentation typo and Ion
 * uses the same 225% cap.
 */
export function getScienceBonus(sciences) {
    const level = (id) => sciences[id] ?? 0;
    return {
        physics: { energyMultiplier: calculatePhysicsMultiplier(level("physics")) },
        chemistry: { fuelReduction: cappedAdditive(level("chemistry"), 0.05, 15) },
        mathematics: {
            resourceProductionMultiplier: calculateMathematicsMultiplier(level("mathematics")),
        },
        astronomy: { shipSpeedMultiplier: linearBonus(level("astronomy"), 0.10, 15) },
        ecology: { ozoneRecoveryPerDay: calculateOzoneRecovery(level("ecology"), 86400) },
        architecture: { buildingCostReduction: calculateBuildingCostReduction(level("architecture")) },
        signalSystems: { maxSimultaneousFlights: Math.ceil(1 + level("signalSystems") * 1.5) },
        rockets: { shipSpeedMultiplier: linearBonus(level("rockets"), 0.15, 15) },
        aerodynamics: { shipSpeedMultiplier: linearBonus(level("aerodynamics"), 0.15, 15) },
        wormholes: { shipSpeedMultiplier: linearBonus(level("wormholes"), 0.20, 15) },
        gravitationalManipulators: { masterUnitsUnlocked: level("gravitationalManipulators") >= 1 },
        intelligence: { revealsAttackerShipTypes: level("intelligence") >= 10 },
        defenceSystems: { shipLifeMultiplier: linearBonus(level("defenceSystems"), 0.10, 20) },
        laser: { laserAttackMultiplier: linearBonus(level("laser"), 0.15, 15) },
        ion: { ionAttackMultiplier: linearBonus(level("ion"), 0.15, 15) },
        plasma: { plasmaAttackMultiplier: linearBonus(level("plasma"), 0.15, 15) },
        polymerShields: { armorBonus: cappedAdditive(level("polymerShields"), 0.01, 10) },
        carbonFiberShields: { armorBonus: cappedAdditive(level("carbonFiberShields"), 0.02, 10) },
        heavyMetalShields: { armorBonus: cappedAdditive(level("heavyMetalShields"), 0.03, 10) },
        offensiveTactics: { shipAttackMultiplier: linearBonus(level("offensiveTactics"), 0.05, 10) },
        defensiveTactics: { shipLifeMultiplier: linearBonus(level("defensiveTactics"), 0.05, 10) },
        surpriseTactics: {
            criticalChance: cappedAdditive(level("surpriseTactics"), SURPRISE_TACTICS_CRIT_CHANCE_PER_LEVEL, 10),
        },
    };
}
//# sourceMappingURL=sciences.js.map